import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';

import { UTILITY_USER_ROLE_ADMIN, UTILITY_USER_ROLE_PARTNER } from 'common/utility/userRoles';
import {
  PMS_NAMES_WITH_PICKER,
  PROPERTY_MANAGEMENT_SOFTWARE_NAME_RENT_MANAGER,
  PROPERTY_MANAGEMENT_SOFTWARE_NAME_RENTVINE,
} from 'common/propertyManagementSoftwareConstants';

import { SCREEN_ID_UTILITY_PARTNER_SETTINGS_INTEGRATIONS } from 'client/constants/screenIds';

import UtilityPartnerActions from 'client/actions/UtilityPartner';

import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedUtilityRoles from 'client/helpers/allowedUtilityRoles';
import branch from 'client/core/branch';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrLink from 'client/components/SrLink/SrLink';
import SrCard, { SR_CARD_BULLET_COLOR_BLUE } from 'client/components/SrCard/SrCard';
import SrPartnerPageV2 from 'client/components/SrPartnerPageV2/SrPartnerPageV2';
import UpPmsUnitTypesPicker from 'client/components/UPPmsUnitTypes/UpPmsUnitTypesPicker';

// Page-level copy/classnames per picker-enabled PMS. The picker itself has its own provider
// map (fetch actions, credential echo); this one only covers the connection-card chrome.
// upSettingsIntegrationsRentManager predates Rentvine support and is a stable E2E hook.
const SETTINGS_PROVIDER_CONFIGS = {
  [PROPERTY_MANAGEMENT_SOFTWARE_NAME_RENT_MANAGER]: {
    displayName: 'Rent Manager',
    sectionClassName: 'upSettingsIntegrationsConnection upSettingsIntegrationsRentManager',
    credentialLabel: 'Company code',
    pickerSourceSentence: 'The housing types below come straight from your Rent Manager.',
  },
  [PROPERTY_MANAGEMENT_SOFTWARE_NAME_RENTVINE]: {
    displayName: 'Rentvine',
    sectionClassName: 'upSettingsIntegrationsConnection upSettingsIntegrationsRentvine',
    // Rentvine also stores an API key/secret on the integration; only the subdomain is
    // safe to display.
    credentialLabel: 'Subdomain',
    pickerSourceSentence: 'The housing types below come straight from Rentvine.',
  },
};

@withTransitionHook
@allowedUtilityRoles({
  roles: [UTILITY_USER_ROLE_ADMIN, UTILITY_USER_ROLE_PARTNER],
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_UTILITY_PARTNER_SETTINGS_INTEGRATIONS)
@withScrollToTop
@branch(({ match }) => ({
  utilityPartner: ['utilityPartners', 'bySlug', match.params.partnerSlug],
  utilityUser: ['utilityUser'],
}))
class UtilityPartnerSettingsIntegrationsPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Integrations',
  });

  static fetchData(match, inject) {
    return UtilityPartnerActions.getBySlug(inject, {
      slug: match.params.partnerSlug,
    });
  }

  static propTypes = {
    utilityPartner: PropTypes.object,
    isTransitioning: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    this.state = {
      integrations: null,
      isLoadingIntegrations: false,
      integrationsError: null,
    };
  }

  componentDidMount() {
    this._loadIntegrations();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.utilityPartner?.id !== this.props.utilityPartner?.id) {
      this._loadIntegrations();
    }
  }

  // The partner's PMS name when it has a configurable integration here (see
  // PMS_NAMES_WITH_PICKER); null for everyone else. Doubles as the picker's `provider` prop.
  _getPickerProvider() {
    const { utilityPartner } = this.props;
    const pmsName = utilityPartner?.propertyManagementSoftware?.name;
    return PMS_NAMES_WITH_PICKER.includes(pmsName) ? pmsName : null;
  }

  _loadIntegrations = () => {
    const { utilityPartner, dispatch } = this.props;

    if (!utilityPartner || !this._getPickerProvider()) {
      return;
    }

    this.setState({ isLoadingIntegrations: true, integrationsError: null });

    dispatch(UtilityPartnerActions.getPMSIntegrations, {
      utilityPartnerId: utilityPartner.id,
      onSuccess: (response) => {
        this.setState({ integrations: response.integrations, isLoadingIntegrations: false });
      },
      onError: (error) => {
        this.setState({
          integrationsError: error.message || 'Failed to load your integration.',
          isLoadingIntegrations: false,
        });
      },
    });
  };

  _onPickerSaved = (updatedIntegration) => {
    this.setState(({ integrations }) => ({
      integrations: (integrations || []).map((integration) =>
        integration.id === updatedIntegration.id ? updatedIntegration : integration,
      ),
    }));
  };

  _getProviderIntegration(provider) {
    const { utilityPartner } = this.props;
    const { integrations } = this.state;

    return (integrations || []).find(
      (integration) =>
        integration.propertyManagementSoftware?.name === provider ||
        integration.propertyManagementSoftwareId ===
          utilityPartner.propertyManagementSoftware?.id,
    );
  }

  _renderNoConfigurableIntegrationNote() {
    const { utilityPartner } = this.props;
    const pmsName = utilityPartner.propertyManagementSoftware?.name;

    return (
      <SrBox className="upSettingsIntegrationsEmptyState" padding="18px 0">
        <SrText font="greycliff" weight="700" size="16">
          {pmsName
            ? `Your PM software (${pmsName}) doesn't have configurable integration settings here yet.`
            : "You haven't connected a PM software yet."}
        </SrText>
        <SrBox marginTop="10px">
          <SrText font="greycliff" weight="500" size="14">
            Head to your{' '}
            <SrLink to={`/utility-profit/${utilityPartner.slug}`} color="blue300">
              overview page
            </SrLink>{' '}
            to set up your PM software sync.
          </SrText>
        </SrBox>
      </SrBox>
    );
  }

  _renderProviderSection(provider) {
    const { utilityPartner } = this.props;
    const { isLoadingIntegrations, integrationsError } = this.state;
    const providerConfig = SETTINGS_PROVIDER_CONFIGS[provider];

    if (isLoadingIntegrations || (!this.state.integrations && !integrationsError)) {
      return (
        <SrText font="greycliff" weight="700" size="14">
          Loading your {providerConfig.displayName} integration…
        </SrText>
      );
    }

    if (integrationsError) {
      return (
        <SrText font="greycliff" weight="700" size="14" color="orange300">
          {integrationsError}
        </SrText>
      );
    }

    const integration = this._getProviderIntegration(provider);

    if (!integration) {
      return this._renderNoConfigurableIntegrationNote();
    }

    const isConnected = integration.enabled === true;

    return (
      <SrBox className={providerConfig.sectionClassName}>
        <SrCard
          className="upSettingsIntegrationsStatus"
          color={SR_CARD_BULLET_COLOR_BLUE}
          header={providerConfig.displayName}
          subheader={
            `Your ${providerConfig.displayName} connection. ` +
            'Changes here apply to future move-ins only.'
          }
        >
          <SrBox marginTop="4px">
            <SrText font="greycliff" weight="500" size={12} color="grey">
              {providerConfig.credentialLabel}
            </SrText>
            <SrBox marginTop="2px">
              <SrText font="greycliff" weight="700" size={14} color="blueGreycliff">
                {integration.subdomain || '—'}
              </SrText>
            </SrBox>
          </SrBox>
          <SrBox
            marginTop="12px"
            display="inlineBlock"
            bgColor={isConnected ? 'green-lighter' : 'orange200'}
            borderRadius="20px"
            padding="4px 10px"
          >
            <SrText
              font="greycliff"
              weight="700"
              size={12}
              color={isConnected ? 'green' : 'orange300'}
            >
              {/* U+2022 bullet — U+25CF renders near cap-height at 12px Greycliff (too heavy) */}
              {isConnected ? '• Connected' : '• Not connected'}
            </SrText>
          </SrBox>
        </SrCard>
        <SrCard
          className="upSettingsIntegrationsPickerCard"
          color={SR_CARD_BULLET_COLOR_BLUE}
          header="Select the housing types to sync"
          subheader={
            'When a new tenant is scheduled to move in, Utility Profit reaches out to them ' +
            `to get their utilities set up before move-in day. ${providerConfig.pickerSourceSentence} ` +
            "We'll only contact move-ins at the ones you check. Unchecked housing types are " +
            "ignored, and we'll never message those tenants."
          }
        >
          <UpPmsUnitTypesPicker
            embedded
            utilityPartner={utilityPartner}
            integration={integration}
            provider={provider}
            onSaved={this._onPickerSaved}
          />
        </SrCard>
      </SrBox>
    );
  }

  render() {
    const { utilityPartner, isTransitioning } = this.props;

    if (isTransitioning) {
      return null;
    }

    if (!utilityPartner) {
      return <Redirect to="/" />;
    }

    const provider = this._getPickerProvider();

    return (
      <SrPartnerPageV2
        backUrl={`/utility-profit/${utilityPartner.slug}/settings`}
        navbarImage={utilityPartner.logoUrl}
        navbarTitle={utilityPartner.name}
        pageHeader="Integrations"
      >
        {provider
          ? this._renderProviderSection(provider)
          : this._renderNoConfigurableIntegrationNote()}
      </SrPartnerPageV2>
    );
  }
}

UtilityPartnerSettingsIntegrationsPage.displayName = 'UtilityPartnerSettingsIntegrationsPage';

export default UtilityPartnerSettingsIntegrationsPage;
